import { PlusOutlined } from '@ant-design/icons';
import { Button, Flex, message, Popconfirm, Space, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useContext, useState } from 'react';
import { useClientServiceGetBackendv2Client, useOutputFormatServiceDeleteBackendv2Outputformat, useOutputFormatServiceGetBackendv2Outputformat } from '../../backendapi/queries';
import { OutputFormatDTO } from '../../backendapi/requests';
import CreateOutputFormat from './CreateOutputFormat';
import EditOutputFormat from './EditOutputFormat';
import { ConfigContext } from '../../contexts/ConfigContext';

const OutputFormatList: React.FC = () => {

    const { currentClient } = useContext(ConfigContext);

    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
    const { mutateAsync: deleteFormat } = useOutputFormatServiceDeleteBackendv2Outputformat();

    const { data, isLoading, refetch } = useOutputFormatServiceGetBackendv2Outputformat({      
        clientId: currentClient.id,     
        top: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),      
        orderby: `${sortField} ${sortOrder}`,
        count: 'true'
    }, undefined, { placeholderData: (prev)=> prev});

    const [selectedOutputFormat, setSelectedOutputFormat] = useState<OutputFormatDTO | undefined>(undefined);

    const [createOpen, setCreateOpen] = useState(false);

    const openCreate = () => {
        setCreateOpen(true);
    };

    const handleCreateClose = useCallback(() => {
        setCreateOpen(false);
        refetch();
    }, [refetch]);

    const handleEditClose = useCallback(() => {
        setSelectedOutputFormat(undefined);
        refetch();
    }, [refetch]);

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<OutputFormatDTO> | SorterResult<OutputFormatDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if (!Array.isArray(sorter)) {
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    /*const handleClientChange = useCallback((value: any, option?: { label: string; value: string; } | { label: string; value: string; }[]) => {
        if (Array.isArray(option)) {
            setSelectedClients(option.map(e => parseInt(e.value)));
        }
    }, []);*/

    const handleDelete = useCallback((format: OutputFormatDTO) => {
        if (format.id) {
            deleteFormat({ key: format.id }).then(() => {
                message.success("Format deleted");
                refetch();
            })
        }
    }, [deleteFormat, refetch]);

    const columns: ColumnsType<OutputFormatDTO> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => a.id - b.id,
        },
       /* {
            title: 'Client',
            dataIndex: 'client',
            key: 'clientId',
            render: (_, record) => (
                clients?.value.find(e => e.id === record.clientId)?.name
            ),
        },*/
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Space size="middle">
                        <Button type="link" onClick={() => setSelectedOutputFormat(record)}>Edit</Button>
                    </Space>
                    <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
                        <Button type="link">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Flex justify='flex-end' style={{ marginBottom: 16 }}>
                <Space>
                    <Button type="primary" onClick={openCreate} icon={<PlusOutlined />} style={{ marginBottom: 16 }}>
                        New configuration
                    </Button>
                </Space>
            </Flex>

            <CreateOutputFormat isOpen={createOpen} onClose={handleCreateClose} />

            <EditOutputFormat isOpen={selectedOutputFormat != null} onClose={handleEditClose} outputFormat={selectedOutputFormat} />

            <Table
                loading={isLoading}
                dataSource={data?.value}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                }} />
        </>
    );
};

export default OutputFormatList;